/**
 * ExtrudeNode - Extrude a 2D shape into 3D geometry
 *
 * Takes a Shape input, extrudes along Z axis.
 * Shape propagation stays — receives shapes from SplineNode/OutlineNode.
 * Output geometry is queried by SceneNode.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import type { IEndpoint } from '@/runtime/IEndpoint';
import type { Payload } from '@/graph/Payload';

const extrudeEndpoints = {
  ...internals({
    shapeIn: { dataType: 'three:shape', isInput: true, label: 'Shape' },
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    depth: { value: 1, min: 0.01, max: 10, step: 0.01 },
    bevelEnabled: { dataType: 'boolean', value: false, label: 'Bevel' },
    bevelThickness: { value: 0.1, min: 0, max: 1, step: 0.01, label: 'Bevel Thickness' },
    bevelSize: { value: 0.1, min: 0, max: 1, step: 0.01, label: 'Bevel Size' },
    bevelSegments: { value: 3, min: 1, max: 10, step: 1, label: 'Bevel Segments' },
    curveSegments: { value: 12, min: 1, max: 64, step: 1, label: 'Curve Segments' }
  })
};

class ExtrudeNode extends BaseNode<typeof extrudeEndpoints> {
  private three: Three;
  private shape: any = null;
  private plane: 'XY' | 'XZ' = 'XY';
  /** Stored mesh reference from SceneNode — for in-place geometry replacement */
  private mesh: any = null;
  /** Callback for geometry pipeline refresh (when upstream of Boolean/Merge) */
  private rebuildCallback: (() => void) | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  /** Build geometry from current shape + params — called by SceneNode */
  buildGeometry(): any {
    if (!this.shape) return null;
    if (typeof this.shape.extractPoints !== 'function') {
      throw new Error(`ExtrudeNode: Invalid shape received`);
    }

    const geometry = new this.three.ExtrudeGeometry(this.shape, {
      depth: this.params.depth.value,
      bevelEnabled: this.params.bevelEnabled.value,
      bevelThickness: this.params.bevelThickness.value,
      bevelSize: this.params.bevelSize.value,
      bevelSegments: this.params.bevelSegments.value,
      curveSegments: this.params.curveSegments.value
    });

    if (this.plane === 'XZ') {
      geometry.rotateX(-Math.PI / 2);
    }

    geometry.computeVertexNormals();
    return geometry;
  }

  /** Store mesh reference from SceneNode for in-place geometry replacement */
  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  /** Store rebuild callback from SceneNode */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  /** Per-frame callback for useExp params — rebuilds geometry each frame */
  animateCallback = () => {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  };

  /** Shape input still propagates from SplineNode/OutlineNode */
  onInternalChanged(payload: Payload, endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'shapeIn') {
      this.shape = payload?.value;
      this.plane = payload?.plane ?? 'XY';
      if (this.mesh) {
        const oldGeo = this.mesh.geometry;
        this.mesh.geometry = this.buildGeometry();
        if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
      }
    }
  }

  onDisconnectedFrom(endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'shapeIn') {
      this.shape = null;
    }
  }

  onParamChanged(): void {
    if (this.rebuildCallback) {
      this.rebuildCallback();
    } else if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  }
}

export const extrudeNodeDef: NodeDef<ExtrudeNode> = {
  type: 'Extrude',
  shortName: 'extrude',
  info: 'Extrudes a 2D shape along the Z axis to create 3D geometry.',
  category: 'shape',
  endpoints: extrudeEndpoints,
  ctor: ExtrudeNode
};
